import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { format, isValid, parse } from 'date-fns';
import { Gender } from '../../../db/enums/misc-enums.js';
import { getSchoolYear, getSchoolYearIntervalDates } from '../../../schoolyear/getSchoolYear.js';

/**
 * Booking runtimes are inclusive canonical calendar days, so an imported day is pinned to the UTC
 * grid rather than to the importing machine's midnight.
 */
function parseBookingDay(value: string | null | undefined): Date | null {
  if (!value) return null;

  const parsedDate = parse(value, 'dd.MM.yyyy', new Date());
  if (!isValid(parsedDate))
    throw new ExpectedError(`Datum muss im Format TT.MM.JJJJ stehen! Aktueller Wert: "${value}"`);

  return toCalendarDay(parsedDate);
}

/**
 * The schoolyear is stored on the booking in its canonical `26 / 27` form, and the import identifies a
 * class booking by exactly that string. An imported `26/27` would find no booking and silently create a
 * second one, so the imported value is normalized through the same helpers that produce the stored one.
 */
export function parseSchoolyear(value: string | null | undefined): string | null {
  if (!value) return null;

  const { start } = getSchoolYearIntervalDates(value);
  if (!start) throw new ExpectedError(`Schuljahr muss zwei aufeinanderfolgende Jahre nennen, z.B. "25 / 26"!`);

  return getSchoolYear({ date: start });
}

/**
 * @entity PersonImportData
 * Proxies the persons import data from the csv fields
 */
@Entity()
export class PersonImportData extends AdornisEntity {
  static override _class = 'PersonImportData';

  constructor(doc: PartialEntityData<PersonImportData>) {
    super(doc);
  }

  /**
   * -------------------------------------------------------
   * Fields which are directly importable on a person and the user can directly use to insert data.
   * -------------------------------------------------------
   */
  @Field(() => String)
  debitorennummer: Maybe<string>;

  @Field(() => String)
  email: Maybe<string>;

  @Field(() => String)
  stammdaten_geschlecht: Maybe<string>;

  @Field(() => Date)
  stammdaten_geburtsDatum: Maybe<Date>;

  @Field(() => String)
  stammdaten_vorsatzWort: Maybe<string>;

  @Field(() => String)
  stammdaten_namenszusatz: Maybe<string>;

  @Field(() => String)
  stammdaten_nachname: Maybe<string>;

  @Field(() => String)
  stammdaten_vorname: Maybe<string>;

  @Field(() => String)
  stammdaten_titel: Maybe<string>;

  @Field(() => String)
  stammdaten_telefonNr: Maybe<string>;

  @Field(() => String)
  adresse_streetAndNumber: Maybe<string>;

  @Field(() => String)
  adresse_zip: Maybe<string>;

  @Field(() => String)
  adresse_city: Maybe<string>;

  @Field(() => String)
  adresse_country: Maybe<string>;

  @Field(() => String)
  adresse_auslandsPlz: Maybe<string>;

  @Field(() => String)
  bankInfos_iban: Maybe<string>;

  @Field(() => String)
  bankInfos_kontoinhaber: Maybe<string>;

  /**
   * The id of the system the file was exported from - the strongest key the import matches by, see
   * `getPersonImportMatch`.
   */
  @Field(() => String)
  externalUniqueID: Maybe<string>;

  /**
   * -------------------------------------------------------
   * Fields used by the user which have custom behavior and are not directly inserted into the database.
   * For example the "klasse" field from the import gets deconstructed on parsing
   * -------------------------------------------------------
   */

  @Field(() => String)
  schoolyear: Maybe<string>;

  /**
   * The runtime of the class booking as canonical calendar days. Can be omitted if a person is not a student.
   * If the start and edn date is not in the importz we derive it from the schoolyear field.
   */
  @Field(() => Date)
  booking_startDate: Maybe<Date>;

  @Field(() => Date)
  booking_endDate: Maybe<Date>;

  @Field(() => String)
  schule_kostenstelle: Maybe<string>;

  @Field(() => String)
  grade: Maybe<string>;

  @Field(() => String)
  classIdentifier: Maybe<string>;

  @Field(() => String)
  legacy_debitorennummer: Maybe<string>;

  /**
   * -------------------------------------------------------
   * Fields injected by the Import.
   * These are not usable by the user and are only used by the import process.
   * -------------------------------------------------------
   */

  /**
   * On a new person, the import sometimes need to predetermine which _id a new person should have.
   * For example if we import a whole family we can already write the childId to the SbS
   */
  @Field(() => String)
  _id: Maybe<string>;

  /**
   * A childId which should be ensured on the "sorgeberechtigteKinderIDs" array of the person
   */
  @Field(() => String)
  childId: Maybe<string>;

  /**
   * Whether the entry is detected as an SbS.
   */
  @Field(() => Boolean)
  isSbS: Maybe<boolean>;

  /**
   * The raw CSV row as a JSON string for raw keyword detection like __DELETE__
   */
  @Field(() => String)
  rawCsvJson: Maybe<string>;
}

/**
 * Defines which fields of the PersonImportData entity can be directly inserted into a person by specifiying the path to the field
 */
export const PERSON_IMPORT_DATA_TO_DB_FIELDS = {
  _id: '_id',
  debitorennummer: 'debitorennummer',
  email: 'email',
  stammdaten_geschlecht: 'profilDaten.stammdaten.geschlecht',
  stammdaten_geburtsDatum: 'profilDaten.stammdaten.geburtsDatum',
  stammdaten_vorsatzWort: 'profilDaten.stammdaten.vorsatzWort',
  stammdaten_namenszusatz: 'profilDaten.stammdaten.namenszusatz',
  stammdaten_nachname: 'profilDaten.stammdaten.nachname',
  stammdaten_vorname: 'profilDaten.stammdaten.vorname',
  stammdaten_titel: 'profilDaten.stammdaten.titel',
  stammdaten_telefonNr: 'profilDaten.stammdaten.telefonNr',
  adresse_streetAndNumber: 'profilDaten.adresse.streetAndNumber',
  adresse_zip: 'profilDaten.adresse.zip',
  adresse_city: 'profilDaten.adresse.city',
  adresse_country: 'profilDaten.adresse.country',
  adresse_auslandsPlz: 'profilDaten.adresse.auslandsPlz',
  bankInfos_iban: 'profilDaten.bankInfos.iban',
  bankInfos_kontoinhaber: 'profilDaten.bankInfos.kontoinhaber',
  externalUniqueID: 'externalUniqueID',
} as const satisfies Partial<Record<keyof PersonImportData, string>>;

export const BOOKING_IMPORT_DATA_TO_DB_FIELDS = {
  booking_startDate: 'startDate',
  booking_endDate: 'endDate',
  grade: 'grade',
  classIdentifier: 'classIdentifier',
} as const satisfies Partial<Record<keyof PersonImportData, string>>;

/**
 * @entity PersonImportConvertConfig
 * Configures the imports csv to entity conversion
 */
@Entity()
export class PersonImportConvertConfig extends ConvertConfig<PersonImportData> {
  static override _class = 'PersonImportConvertConfig';

  constructor(doc: PartialEntityData<PersonImportConvertConfig>) {
    super({
      ...doc,
      delimiter: ';',
      className: PersonImportData._class,
      encoding: 'utf-8',
      uniqueIdentifierFieldName: 'debitorennummer',
      foreignKeys: [],
      fieldMappings: [
        new FieldMappingEntry({ optional: true, input: 'debitorennummer', output: 'debitorennummer' }, value => {
          if (!value) return null;

          /**
           * Check if value has length 7 and consists only of numbers
           * @throws Error if the value is not exactly 7 digits
           */
          if (value.length !== 7 || !/^\d{7}$/.test(value)) {
            throw new ExpectedError(`Debitorennummer muss exakt 7 Ziffern haben! Aktueller Wert: "${value}"`);
          }

          /**
           * Values from 7.000.000 up are creditors
           */
          if (parseInt(value, 10) > 6_999_999) {
            throw new ExpectedError(`Debitorennummer muss kleiner als 7000000 sein! Aktueller Wert: "${value}"`);
          }

          return value;
        }),
        new FieldMappingEntry({ optional: true, input: 'externalUniqueID', output: 'externalUniqueID' }),
        new FieldMappingEntry({ optional: true, input: 'email', output: 'email' }),
        new FieldMappingEntry(
          { optional: true, input: 'stammdaten_geschlecht', output: 'stammdaten_geschlecht' },
          value => {
            if (!value) return null;
            switch (value) {
              case 'M':
                return Gender.MALE;
              case 'F':
                return Gender.FEMALE;
              case 'D':
                return Gender.DIVERS;
              default:
                return Gender.UNKNOWN;
            }
          },
        ),
        new FieldMappingEntry(
          { optional: true, input: 'stammdaten_geburtsDatum', output: 'stammdaten_geburtsDatum' },
          value => {
            if (!value) return null;

            const parsedDate = parse(value, 'dd.MM.yyyy', new Date());
            return isValid(parsedDate) ? parsedDate : null;
          },
        ),
        new FieldMappingEntry({ optional: true, input: 'stammdaten_vorsatzWort', output: 'stammdaten_vorsatzWort' }),
        new FieldMappingEntry({ optional: true, input: 'stammdaten_namenszusatz', output: 'stammdaten_namenszusatz' }),
        new FieldMappingEntry({ optional: true, input: 'stammdaten_nachname', output: 'stammdaten_nachname' }),
        new FieldMappingEntry({ optional: true, input: 'stammdaten_vorname', output: 'stammdaten_vorname' }),
        new FieldMappingEntry({ optional: true, input: 'stammdaten_titel', output: 'stammdaten_titel' }),
        new FieldMappingEntry({ optional: true, input: 'stammdaten_telefonNr', output: 'stammdaten_telefonNr' }),
        new FieldMappingEntry({ optional: true, input: 'adresse_streetAndNumber', output: 'adresse_streetAndNumber' }),
        new FieldMappingEntry({ optional: true, input: 'adresse_zip', output: 'adresse_zip' }),
        new FieldMappingEntry({ optional: true, input: 'adresse_city', output: 'adresse_city' }),
        new FieldMappingEntry({ optional: true, input: 'adresse_country', output: 'adresse_country' }),
        new FieldMappingEntry({ optional: true, input: 'adresse_auslandsPlz', output: 'adresse_auslandsPlz' }),
        new FieldMappingEntry({ optional: true, input: 'bankInfos_iban', output: 'bankInfos_iban' }, value =>
          value?.replaceAll(' ', ''),
        ),
        new FieldMappingEntry({ optional: true, input: 'schule_kostenstelle', output: 'schule_kostenstelle' }),
        new FieldMappingEntry({ optional: true, input: 'bankInfos_kontoinhaber', output: 'bankInfos_kontoinhaber' }),
        new FieldMappingEntry({ optional: true, input: 'alte_debitorennummer', output: 'legacy_debitorennummer' }),
        new FieldMappingEntry({ optional: true, input: 'jahrgangsstufe', output: 'grade' }),
        new FieldMappingEntry({ optional: true, input: 'klasse', output: 'classIdentifier' }),
        new FieldMappingEntry({ optional: true, input: 'startdatum', output: 'booking_startDate' }, parseBookingDay),
        new FieldMappingEntry({ optional: true, input: 'enddatum', output: 'booking_endDate' }, parseBookingDay),
        new FieldMappingEntry({ optional: true, input: 'schuljahr', output: 'schoolyear' }, parseSchoolyear),
      ],
    });
  }
}

/**
 * How the import's fields read in the overview - the edit dialog, the conflicts of merged rows.
 */
export const IMPORT_FIELD_LABELS: Partial<Record<keyof PersonImportData, string>> = {
  externalUniqueID: 'Externe ID',
  debitorennummer: 'Debitorennummer',
  legacy_debitorennummer: 'Alte Debitorennummer',
  email: 'E-Mail',
  stammdaten_titel: 'Titel',
  stammdaten_vorname: 'Vorname',
  stammdaten_vorsatzWort: 'Vorsatzwort',
  stammdaten_nachname: 'Nachname',
  stammdaten_namenszusatz: 'Namenszusatz',
  stammdaten_geschlecht: 'Geschlecht (M, F, D)',
  stammdaten_geburtsDatum: 'Geburtsdatum (TT.MM.JJJJ)',
  stammdaten_telefonNr: 'Telefon',
  adresse_streetAndNumber: 'Straße und Hausnummer',
  adresse_zip: 'PLZ',
  adresse_city: 'Ort',
  adresse_country: 'Land',
  adresse_auslandsPlz: 'Ausländische PLZ',
  bankInfos_iban: 'IBAN',
  bankInfos_kontoinhaber: 'Kontoinhaber',
  schule_kostenstelle: 'Kostenstelle der Schule',
  schoolyear: 'Schuljahr',
  grade: 'Jahrgangsstufe',
  classIdentifier: 'Klasse',
  booking_startDate: 'Buchungsbeginn (TT.MM.JJJJ)',
  booking_endDate: 'Buchungsende (TT.MM.JJJJ)',
};

/** the fields of the class booking - meaningless on a guardian's row */
export const IMPORT_BOOKING_FIELDS: (keyof PersonImportData)[] = [
  'schoolyear',
  'grade',
  'classIdentifier',
  'booking_startDate',
  'booking_endDate',
];

/**
 * The csv columns a user may correct in the overview, with the field each one fills: every column the
 * file can carry. What the import injects itself (`_id`, `childId`, `isSbS`, the raw row) is no column
 * and therefore never editable.
 */
export function getEditableImportColumns(): { column: string; field: keyof PersonImportData }[] {
  return new PersonImportConvertConfig({}).fieldMappings
    .filter(mapping => mapping.input && mapping.output)
    .map(mapping => ({ column: mapping.input as string, field: mapping.output as keyof PersonImportData }));
}

const GENDER_CSV_VALUE: Partial<Record<string, string>> = {
  [Gender.MALE]: 'M',
  [Gender.FEMALE]: 'F',
  [Gender.DIVERS]: 'D',
};

/**
 * A field of the import data the way a csv cell carries it, so that parsing it again through the column's
 * mapping returns the same value - the edit dialog shows and sends exactly this.
 */
export function importValueToCsv(data: PersonImportData, field: keyof PersonImportData): string {
  const value = data[field];
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return format(value, 'dd.MM.yyyy');
  if (field === 'stammdaten_geschlecht') return GENDER_CSV_VALUE[String(value)] ?? '';
  return String(value);
}
